import { useEffect, useRef, useState } from 'react'
import { loadGoogleMaps, getGoogleMapsApiKey, STORYFY_MAP_STYLE } from '../lib/googleMaps'

const TBILISI = { lat: 41.7151, lng: 44.8271 }

type Props = {
  latitude: number | null
  longitude: number | null
  onPick: (p: { latitude: number; longitude: number; location?: string }) => void
  language?: string
  className?: string
}

/**
 * Interactive, brand-styled location picker. The host taps the map (or drags the
 * pin) to drop a marker; the coordinates are reported up and the address is
 * reverse-geocoded. Renders nothing when Maps is unavailable so the caller can
 * fall back to the static embed.
 */
const LocationPickerMap = ({
  latitude,
  longitude,
  onPick,
  language = 'en',
  className = '',
}: Props) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<any>(null)
  const markerRef = useRef<any>(null)
  const geocoderRef = useRef<any>(null)
  const onPickRef = useRef(onPick)
  onPickRef.current = onPick
  const [status, setStatus] = useState<'loading' | 'ready' | 'unavailable'>('loading')

  useEffect(() => {
    let cancelled = false
    const key = getGoogleMapsApiKey()
    if (!key) {
      setStatus('unavailable')
      return
    }

    loadGoogleMaps(key)
      .then((ok) => {
        if (cancelled) return
        const g = (window as any).google
        if (!ok || !g?.maps || !containerRef.current) {
          setStatus('unavailable')
          return
        }

        const hasPoint = latitude != null && longitude != null
        const center = hasPoint
          ? { lat: latitude as number, lng: longitude as number }
          : TBILISI

        const map = new g.maps.Map(containerRef.current, {
          center,
          zoom: hasPoint ? 15 : 12,
          styles: STORYFY_MAP_STYLE,
          disableDefaultUI: true,
          zoomControl: true,
          clickableIcons: false,
          gestureHandling: 'greedy',
        })
        const geocoder = new g.maps.Geocoder()
        const marker = new g.maps.Marker({
          map,
          position: center,
          draggable: true,
          visible: hasPoint,
        })

        mapRef.current = map
        markerRef.current = marker
        geocoderRef.current = geocoder

        const emit = (latLng: any) => {
          const lat = latLng.lat()
          const lng = latLng.lng()
          marker.setPosition({ lat, lng })
          marker.setVisible(true)
          geocoder.geocode(
            { location: { lat, lng } },
            (results: any, gStatus: string) => {
              const location =
                gStatus === 'OK' && results?.[0]?.formatted_address
                  ? results[0].formatted_address
                  : undefined
              onPickRef.current({ latitude: lat, longitude: lng, location })
            },
          )
        }

        map.addListener('click', (e: any) => emit(e.latLng))
        marker.addListener('dragend', (e: any) => emit(e.latLng))
        setStatus('ready')
      })
      .catch(() => {
        if (!cancelled) setStatus('unavailable')
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reflect external coordinate changes (e.g. Places autocomplete) on the map
  // without re-emitting an onPick.
  useEffect(() => {
    if (status !== 'ready' || !mapRef.current || !markerRef.current) return
    if (latitude == null || longitude == null) return
    const pos = { lat: latitude, lng: longitude }
    markerRef.current.setPosition(pos)
    markerRef.current.setVisible(true)
    mapRef.current.panTo(pos)
  }, [latitude, longitude, status])

  if (status === 'unavailable') return null

  return (
    <div className={className}>
      <div
        ref={containerRef}
        className="h-72 w-full rounded-2xl overflow-hidden border border-[#1b1033]/10 dark:border-white/10 bg-[#fff7f2] dark:bg-[#130b26]"
        data-testid="location-picker-map"
      />
      <p className="mt-2 text-xs text-[#4a3d66] dark:text-gray-400">
        {language === 'ka'
          ? 'დააჭირე რუკას ან გადაათრიე პინი ზუსტი ლოკაციის მოსანიშნად'
          : 'Tap the map or drag the pin to mark the exact location'}
      </p>
    </div>
  )
}

export default LocationPickerMap
